import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { AI_RESPONSE_TYPES } from 'constants/AI';
import { FOR_INVESTORS_SECTIONS } from 'constants/businessToolSections/forInvestors';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { FormField } from 'components/_basic/Form/FormField';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { BorderedCard } from 'components/BorderedCard';
import styles from 'components/BusinessToolForm/Sections/AboutCompany/SWOT/SWOT.module.scss';
import { SwotAnalysisRow } from 'components/BusinessToolForm/Sections/AboutCompany/SWOT/SwotAnalysisRow';
import {
  ABOUT_COMPANY_SWOT_NAMES,
  analysisRowInitialValues,
  prefix,
} from 'components/BusinessToolForm/Sections/AboutCompany/SWOT/utils/form';
import { GLOBAL_FIELD_NAMES, globalPrefix } from 'components/BusinessToolForm/Sections/Global/form';

import { concatWithDot } from 'helpers/string';

export const SWOTAnalysis: React.FC = () => {
  const { t } = useTranslation();

  const canAccess = true;

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          title={t(`chapters.${FOR_INVESTORS_SECTIONS.COMPANY_AND_PROJECT}`)}
          badgeText={t('company.swot.title')}
          section={FOR_INVESTORS_SECTIONS.COMPANY_AND_PROJECT}
          locked={!canAccess}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('company.swot.swotAnalysis')}
        aiFieldType={AI_RESPONSE_TYPES.COMPLEX}
        aiFieldName={prefix}
        collapsible
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <BorderedCard>
              <FormField disabled={!canAccess} label={t('strength')}>
                <FieldArrayComponent
                  name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_STRENGTHS)}
                  fieldArrayStyles={{ rowContainer: styles.rowContainer }}
                  initialItem={analysisRowInitialValues}
                  buttonText={t('strength')}
                  disabled={!canAccess}
                  maxLength={4}
                >
                  {(namePrefix) => <SwotAnalysisRow hiddenLabel={true} name={namePrefix} disabled={!canAccess} />}
                </FieldArrayComponent>
              </FormField>
            </BorderedCard>
          </Grid>
          <Grid item xs={12}>
            <BorderedCard>
              <FormField disabled={!canAccess} label={t('opportunity')}>
                <FieldArrayComponent
                  name={concatWithDot(prefix, ABOUT_COMPANY_SWOT_NAMES.OPPORTUNITIES)}
                  fieldArrayStyles={{ rowContainer: styles.rowContainer }}
                  initialItem={analysisRowInitialValues}
                  buttonText={t('opportunity')}
                  disabled={!canAccess}
                  maxLength={4}
                >
                  {(namePrefix) => <SwotAnalysisRow hiddenLabel={true} name={namePrefix} disabled={!canAccess} />}
                </FieldArrayComponent>
              </FormField>
            </BorderedCard>
          </Grid>
          <Grid item xs={12}>
            <BorderedCard>
              <FormField disabled={!canAccess} label={t('weakness')}>
                <FieldArrayComponent
                  name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_WEAKNESSES)}
                  fieldArrayStyles={{ rowContainer: styles.rowContainer }}
                  initialItem={analysisRowInitialValues}
                  buttonText={t('weakness')}
                  disabled={!canAccess}
                  maxLength={4}
                >
                  {(namePrefix) => <SwotAnalysisRow hiddenLabel={true} name={namePrefix} disabled={!canAccess} />}
                </FieldArrayComponent>
              </FormField>
            </BorderedCard>
          </Grid>
          <Grid item xs={12}>
            <BorderedCard>
              <FormField disabled={!canAccess} label={t('threat')}>
                <FieldArrayComponent
                  name={concatWithDot(prefix, ABOUT_COMPANY_SWOT_NAMES.THREATS)}
                  fieldArrayStyles={{ rowContainer: styles.rowContainer }}
                  initialItem={analysisRowInitialValues}
                  buttonText={t('threat')}
                  disabled={!canAccess}
                  maxLength={4}
                >
                  {(namePrefix) => <SwotAnalysisRow hiddenLabel={true} name={namePrefix} disabled={!canAccess} />}
                </FieldArrayComponent>
              </FormField>
            </BorderedCard>
          </Grid>
        </Grid>
      </FormSectionCard>
    </>
  );
};
